import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { FieldValues, useFieldArray, useFormContext } from 'react-hook-form';
import { DragDropContext, Droppable, Draggable } from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration';
import Iconify from 'src/components/iconify';
import { ReactElement, ReactNode, useCallback } from 'react';
import { useI18n } from 'iomart-hooks';

interface ChildrenProps {
  item: FieldValues;
  index: number;
}

interface Setting {
  name: string;
  label?: string;
  format?: (product: any) => string | ReactElement;
}

interface RHFArrayBlockProps {
  children: (props: ChildrenProps) => ReactNode;
  name: string;
  addTitle?: string;
  disabled?: boolean;
  /** Данные для новой строки; может быть функцией, чтобы генерировать уникальные поля (напр. imageSlotId). */
  appendData?: Object | (() => Object);
  settings?: Setting[];
  withAdd?: boolean;
  withRemove?: boolean;
  /** Включить drag-and-drop для изменения порядка элементов */
  draggable?: boolean;
  /** Переопределение grid для контента элемента (по умолчанию на sm — 2 колонки). Например { sm: '1fr' } для полной ширины на sm. */
  itemGridTemplateColumns?: { xs?: string; sm?: string; md?: string };
  /** Позволяет родителю получить функцию replace() из useFieldArray для внешних операций (например, импорт из PDF). */
  onRegisterReplace?: (fn: (items: any[]) => void) => void;
}

const DEFAULT_ITEM_GRID = {
  xs: 'repeat(1, 1fr)',
  sm: 'repeat(2, 1fr)',
  md: 'repeat(auto-fit, minmax(200px, 1fr))',
};

function renderItemContent(
  item: FieldValues,
  index: number,
  children: (props: ChildrenProps) => ReactNode,
  settings: Setting[],
  t: (key: string) => string,
  disabled: boolean,
  withRemove: boolean,
  onRemove: (i: number) => void,
  itemGridTemplateColumns?: { xs?: string; sm?: string; md?: string }
) {
  const gridCols = { ...DEFAULT_ITEM_GRID, ...itemGridTemplateColumns };
  return (
    <>
      <Box
        rowGap={3}
        columnGap={2}
        gridTemplateColumns={gridCols}
        display="grid"
        sx={{ mb: 1 }}
      >
        {children({ item, index })}
      </Box>
      {settings.map(({ name: sName, label: sLabel, format }, sIdx) => (
        <Stack key={sIdx} alignItems="flex-end">
          <Stack direction="row" spacing={1}>
            <Typography paragraph variant="caption" sx={{ color: 'text.disabled', mb: 0 }}>
              {t(sLabel ?? '')}:
            </Typography>
            <Typography variant="caption">{format ? format(item) : item[sName]}</Typography>
          </Stack>
        </Stack>
      ))}
      {!disabled && withRemove && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: settings.length > 0 ? 1 : 0 }}>
          <Button
            size="small"
            color="error"
            startIcon={<Iconify icon="eva:trash-2-outline" />}
            onClick={() => onRemove(index)}
          >
            {t('Remove')}
          </Button>
        </Box>
      )}
    </>
  );
}

export const RHFArrayBlock: React.FC<RHFArrayBlockProps> = ({
  children,
  name,
  addTitle = 'Add new item',
  disabled,
  appendData = {},
  settings = [],
  withAdd = true,
  withRemove = true,
  draggable = false,
  itemGridTemplateColumns,
  onRegisterReplace,
}) => {
  const { t } = useI18n(false);
  const { control, clearErrors, getValues, setValue } = useFormContext();

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name,
  });

  if (onRegisterReplace) {
    onRegisterReplace(replace);
  }

  const handleAdd = useCallback(() => {
    const data = typeof appendData === 'function' ? appendData() : appendData ?? {};
    append(data);
  }, [append, appendData]);

  const handleRemove = useCallback(
    (index: number) => {
      const current = getValues(name);
      if (!Array.isArray(current) || index < 0 || index >= current.length) return;
      const next = current.filter((_, i) => i !== index);
      replace(next);
      clearErrors(name);
    },
    [getValues, name, replace, clearErrors]
  );

  const handleDragEnd = useCallback(
    (result: { destination?: { index: number }; source: { index: number } }) => {
      if (!result.destination || result.destination.index === result.source.index) return;
      const current = getValues(name);
      if (!Array.isArray(current)) return;
      const reordered = [...current];
      const [removed] = reordered.splice(result.source.index, 1);
      reordered.splice(result.destination!.index, 0, removed);
      replace(reordered);
      clearErrors(name);
    },
    [getValues, replace, clearErrors, name]
  );

  const listContent = (
    <Stack divider={<Divider flexItem sx={{ borderStyle: 'dashed' }} />} spacing={3}>
      {fields.map((item, index) => {
        const itemBody = (
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            {draggable && !disabled && (
              <Stack spacing={0.5} alignItems="center" sx={{ pt: 0.5, flexShrink: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    cursor: 'grab',
                    color: 'text.secondary',
                    '&:active': { cursor: 'grabbing' },
                  }}
                >
                  <Iconify icon="eva:move-fill" width={20} />
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  #{index + 1}
                </Typography>
              </Stack>
            )}
            <Stack flex={1} sx={{ minWidth: 0 }}>
              {renderItemContent(item, index, children, settings, t, !!disabled, withRemove, handleRemove, itemGridTemplateColumns)}
            </Stack>
          </Stack>
        );

        if (draggable && !disabled) {
          return (
            <Draggable key={item.id} draggableId={String(item.id)} index={index}>
              {(provided: any) => {
                const { onDragOver, onDrop, ...restDraggableProps } = provided.draggableProps || {};
                const isFileDrag = (e: React.DragEvent) => e.dataTransfer?.types?.includes('Files');
                return (
                  <div
                    ref={provided.innerRef}
                    {...restDraggableProps}
                    onDragOver={(e: React.DragEvent) => {
                      if (isFileDrag(e)) return;
                      onDragOver?.(e);
                    }}
                    onDrop={(e: React.DragEvent) => {
                      if (isFileDrag(e)) return;
                      onDrop?.(e);
                    }}
                  >
                    <Stack direction="row" alignItems="flex-start" spacing={1}>
                      <Stack
                        spacing={0.5}
                        alignItems="center"
                        sx={{ pt: 0.5, flexShrink: 0 }}
                        {...provided.dragHandleProps}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            cursor: 'grab',
                            color: 'text.secondary',
                            '&:active': { cursor: 'grabbing' },
                          }}
                        >
                          <Iconify icon="eva:move-fill" width={20} />
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          #{index + 1}
                        </Typography>
                      </Stack>
                      <Stack flex={1} sx={{ minWidth: 0 }}>
                        {renderItemContent(
                          item,
                          index,
                          children,
                          settings,
                          t,
                          !!disabled,
                          withRemove,
                          handleRemove,
                          itemGridTemplateColumns
                        )}
                      </Stack>
                    </Stack>
                  </div>
                );
              }}
            </Draggable>
          );
        }

        return (
          <Stack key={item.id}>
            {itemBody}
          </Stack>
        );
      })}
    </Stack>
  );

  const wrappedList = draggable && !disabled ? (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={`droppable-${name}`}>
        {(provided: any) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {listContent}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  ) : (
    listContent
  );

  return (
    <>
      {wrappedList}
      {!disabled && withAdd && (
        <Stack
          spacing={2}
          direction={{ xs: 'column-reverse', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          sx={{ mt: fields.length > 1 ? 0 : 3 }}
        >
          <Button
            size="small"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={handleAdd}
            sx={{ flexShrink: 0 }}
          >
            {t(addTitle)}
          </Button>
        </Stack>
      )}
    </>
  );
};
